import { type NodeProps, Position } from '@xyflow/react';
import EditIcon from '@mui/icons-material/Edit';
import { v4 as uuidv4 } from 'uuid';

import type { NodeItem, ParentNode } from '@models/flows';
import { Connector } from '@utils/flows';

import { useToast } from '@hooks/useToast';
import { useNodeError } from '@hooks/useNodeError';

import {
  AddButton,
  ConnectionPoint,
  DeleteButton,
  ErrorToolTip,
} from '@components/Flows/components/partials/index';

import { initialTransactionNatureNode } from '@features/chargerFlows/validation/validation-helpers';

import s from './TransactionNatureNode.module.css';

type TransactionNatureBlockProps = {
  nodeItem: NodeItem;
  onEdit: (code: string) => void;
};

const TransactionNatureBlock = ({
  nodeItem: { id, label },
  onEdit,
}: TransactionNatureBlockProps) => {
  return (
    <div className={s.transactionNatureBlock}>
      <div className={s.transactionNatureLineWrapper}>
        <p className={s.transactionNatureLabel}>{label}</p>
      </div>
      <button type="button" className={s.editButton} onClick={() => onEdit(id)}>
        <EditIcon />
      </button>
      <ConnectionPoint type={Connector.Source} position={Position.Right} id={id} />
    </div>
  );
};

type TransactionNatureProps = NodeProps & {
  onEdit: (parentNode: ParentNode, childNode: NodeItem) => void;
  onChange: (parentNode: ParentNode, childNode: NodeItem) => void;
  onDelete: (nodeId: string) => void;
};

const getTransactionNatureNodeItem = (): NodeItem => ({ id: uuidv4(), value: [''] });

function TransactionNatureNode({
  id,
  type,
  data,
  onEdit,
  onChange,
  onDelete,
}: TransactionNatureProps) {
  const toast = useToast();
  const testGroupsData = data as unknown as { items: NodeItem[] };
  const nodeItems = testGroupsData?.items || [initialTransactionNatureNode];
  const nodeId = id;
  const parentNode: ParentNode = {
    id: nodeId,
    type,
    data: testGroupsData,
  };
  const errors = useNodeError(nodeId);

  const handleNewNode = () => {
    onChange(parentNode, getTransactionNatureNodeItem());
  };

  const handleEditNode = (nodeItemId: string) => {
    const editableItem = nodeItems.find((item) => item.id === nodeItemId);

    if (!editableItem) {
      toast.error('Test group item not found');
      return;
    }

    onEdit(parentNode, editableItem);
  };

  return (
    <div className={`${s.transactionNatureContainer} ${errors ? s.errorBorder : ''}`}>
      <ConnectionPoint type={Connector.Target} position={Position.Left} id="incoming" />
      <h1 className={s.title}>Transaction Nature Node</h1>
      <ErrorToolTip isErrorVisible={Boolean(errors)} messages={errors?.messages} />
      <DeleteButton onDelete={() => onDelete(nodeId)} />
      <div className={s.transactionNatureList}>
        {nodeItems.length > 0 &&
          nodeItems.map((item) => (
            <TransactionNatureBlock key={item.id} nodeItem={item} onEdit={handleEditNode} />
          ))}
      </div>
      <AddButton handleClick={handleNewNode} />
    </div>
  );
}

export default TransactionNatureNode;
